import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { format } from "date-fns";
import { it as itLocale } from "date-fns/locale";
import { Plus, CreditCard, Package, Truck, PackageCheck, Clock, ChevronDown, ChevronUp, ExternalLink, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";
import RequestCardDialog from "@/components/RequestCardDialog";
import OrderBreakdown from "@/components/OrderBreakdown";
import CardPricingCard from "@/components/CardPricingCard";
import { REQ } from "@/constants/testIds/cardRequests";

const STATUS_META = {
  pending: { label: "Richiesta ricevuta", color: "bg-[#E9A94E]/12 text-[#B57F1F]", dot: "bg-[#E9A94E]" },
  approved: { label: "Approvata", color: "bg-[#4A8A6E]/12 text-[#3E7A5E]", dot: "bg-[#4A8A6E]" },
  ordered: { label: "Ordinata", color: "bg-[#4A8A6E]/12 text-[#3E7A5E]", dot: "bg-[#4A8A6E]" },
  production: { label: "In produzione", color: "bg-[#5B7CFB]/12 text-[#4560D8]", dot: "bg-[#5B7CFB]" },
  shipped: { label: "Spedita", color: "bg-[#00F2F4]/12 text-[#00D6D8]", dot: "bg-[#00F2F4]" },
  delivered: { label: "Consegnata", color: "bg-[#0F5132]/12 text-[#0F5132]", dot: "bg-[#0F5132]" },
  cancelled: { label: "Annullata", color: "bg-[#C6423B]/12 text-[#C6423B]", dot: "bg-[#C6423B]" },
};

function SummaryCard({ label, value, icon: Icon, testId }) {
  return (
    <div
      data-testid={testId}
      className="p-4 rounded-lg bg-[var(--bg-3)] border border-[var(--border-c)]"
    >
      <div className="text-[10px] font-sans uppercase tracking-[0.2em] text-[var(--fg-3)] flex items-center gap-1.5">
        <Icon size={11} strokeWidth={1.8} /> {label}
      </div>
      <div className="mt-1.5 font-serif text-3xl text-[var(--fg-1)] leading-none">{value}</div>
    </div>
  );
}

export default function MyCardRequestsSection({ user, businesses }) {
  const [summary, setSummary] = useState(null);
  const [requests, setRequests] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  const load = useCallback(async () => {
    try {
      const [s, r] = await Promise.all([
        api.get("/card-requests/mine/summary"),
        api.get("/card-requests"),
      ]);
      setSummary(s.data);
      setRequests(r.data);
    } catch (err) {
      console.debug("card-requests load failed:", err?.response?.status || err?.message);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <motion.section
      initial={{ y: 16, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      className="bg-[var(--bg-2)] border border-[var(--border-c)] rounded-xl p-8 shadow-[0_2px_12px_rgba(45,41,38,0.04)]"
      data-testid={REQ.section}
    >
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <div className="text-[10px] font-sans uppercase tracking-[0.2em] text-[var(--fg-3)]">
            Le tue RecenUp Card
          </div>
          <h2 className="mt-1 font-serif text-2xl text-[var(--fg-1)] tracking-tight">
            Gestisci e richiedi nuove card
          </h2>
        </div>
        <button
          data-testid={REQ.openBtn}
          onClick={() => setDialogOpen(true)}
          className="inline-flex items-center gap-2 rounded-full bg-[#00F2F4] hover:bg-[#00D6D8] text-[#0A0A0A] px-5 py-2.5 text-sm font-sans font-semibold whitespace-nowrap"
          style={{ transition: "background-color 0.2s ease" }}
        >
          <Plus size={16} strokeWidth={2} /> Richiedi nuove card
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <SummaryCard label="Attive" value={summary?.cards_active ?? "—"} icon={CreditCard} testId={REQ.summary.active} />
        <SummaryCard label="Richieste" value={summary?.requested ?? "—"} icon={Clock} testId={REQ.summary.requested} />
        <SummaryCard label="In produzione" value={summary?.in_production ?? "—"} icon={Package} testId={REQ.summary.production} />
        <SummaryCard label="Spedite" value={summary?.shipped ?? "—"} icon={Truck} testId={REQ.summary.shipped} />
        <SummaryCard label="Consegnate" value={summary?.delivered ?? "—"} icon={PackageCheck} testId={REQ.summary.delivered} />
      </div>

      <CardPricingCard onPayNow={() => setDialogOpen(true)} />

      <div className="mt-8">
        <div className="text-[10px] font-sans uppercase tracking-[0.2em] text-[var(--fg-3)] mb-3">
          Storico richieste
        </div>
        {requests.length === 0 ? (
          <div
            data-testid={REQ.emptyState}
            className="p-8 text-center text-sm text-[var(--fg-3)] border border-dashed border-[var(--border-c)] rounded-lg"
          >
            Nessuna richiesta ancora inviata. Clicca <span className="font-semibold text-[var(--fg-1)]">"Richiedi nuove card"</span> per iniziare.
          </div>
        ) : (
          <div className="divide-y divide-[var(--border-c)] border border-[var(--border-c)] rounded-lg overflow-hidden">
            {requests.map((r) => {
              const meta = STATUS_META[r.status] || STATUS_META.pending;
              const expanded = expandedId === r.request_id;
              return (
                <div key={r.request_id} data-testid={REQ.row(r.request_id)}>
                  <button
                    type="button"
                    onClick={() => setExpandedId(expanded ? null : r.request_id)}
                    className="w-full flex flex-wrap items-center justify-between gap-3 px-4 py-3.5 hover:bg-[var(--bg-3)]/50 text-left"
                    style={{ transition: "background-color 0.15s ease" }}
                  >
                    <div className="flex items-center gap-4">
                      <span className="font-mono text-xs text-[var(--fg-1)] font-semibold">
                        #{r.request_id}
                      </span>
                      <span className="text-sm text-[var(--fg-1)]">
                        {r.quantity} card
                        {r.is_custom && <span className="text-[var(--fg-4)] text-xs ml-1">(personalizzata)</span>}
                      </span>
                      <span className="text-xs text-[var(--fg-3)]">
                        {r.created_at ? format(new Date(r.created_at), "d MMM yyyy", { locale: itLocale }) : "—"}
                      </span>
                      {r.pricing?.total !== undefined && (
                        <span className="text-xs text-[var(--fg-2)] font-mono">
                          Totale € {Number(r.pricing.total).toFixed(2).replace(".", ",")}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3">
                      <span
                        data-testid={REQ.statusPill(r.request_id)}
                        className={`inline-flex items-center gap-1.5 text-[10px] font-sans font-semibold uppercase tracking-[0.15em] px-3 py-1 rounded-full ${meta.color}`}
                      >
                        <span className={`h-1.5 w-1.5 rounded-full ${meta.dot}`} />
                        {meta.label}
                      </span>
                      {expanded ? <ChevronUp size={14} className="text-[var(--fg-3)]" /> : <ChevronDown size={14} className="text-[var(--fg-3)]" />}
                    </div>
                  </button>
                  {expanded && r.pricing && (
                    <div className="px-4 pb-4 space-y-3">
                      <OrderBreakdown pricing={r.pricing} quantity={r.quantity} compact />
                      {r.payment_status === "paid" ? (
                        <div
                          data-testid={`client-payment-paid-${r.request_id}`}
                          className="flex items-center gap-2 text-xs text-[#3E7A5E] font-sans"
                        >
                          <CheckCircle2 size={14} /> Pagamento ricevuto — grazie!
                        </div>
                      ) : r.payment_url ? (
                        <a
                          href={r.payment_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`client-pay-btn-${r.request_id}`}
                          className="inline-flex items-center justify-center gap-2 rounded-full bg-[#00F2F4] hover:bg-[#00D6D8] text-[#0A0A0A] px-5 py-2.5 text-sm font-sans font-semibold w-full md:w-auto"
                          style={{ transition: "background-color 0.2s ease" }}
                        >
                          <ExternalLink size={14} /> Vai al pagamento — Carta / Apple Pay / Google Pay
                        </a>
                      ) : null}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <RequestCardDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        businesses={businesses}
        user={user}
        defaultBusinessId={businesses?.[0]?.business_id}
        onCreated={() => load()}
      />
    </motion.section>
  );
}
